
import type { PropType } from 'vue';

import {
  defineComponent,
  computed,
} from 'vue';

import PrimeVueInputNumber from 'primevue/inputnumber';

import type { ErrorsMap } from '@/types';

// PrimeVue InputNumber calls event.preventDefault() unconditionally inside its keypress
// handler. In Safari/WebKit, Cmd/Ctrl+V dispatches a `keypress` event, and preventing its
// default cancels the native paste command — the `paste` event then never fires, so Cmd+V
// silently does nothing (while right-click → Paste, which has no keypress, works). We can't
// fix this from the outside via stopPropagation (that also cancels the paste in WebKit), so
// we patch the shared component method to skip its handling for modifier combinations. This
// lets clipboard/navigation shortcuts (Cmd/Ctrl + V/C/X/A) behave natively in every
// InputNumber instance, and never inserts a character for those combos anyway.
const inputNumberMethods = (PrimeVueInputNumber as unknown as {
  methods: { onInputKeyPress(event: KeyboardEvent): void };
}).methods;

const originalOnInputKeyPress = inputNumberMethods.onInputKeyPress;

inputNumberMethods.onInputKeyPress = function onInputKeyPress(
  this: unknown,
  event: KeyboardEvent,
): void {
  if (event.metaKey || event.ctrlKey) {
    return;
  }

  originalOnInputKeyPress.call(this, event);
};

export default defineComponent({
  components: {
    PrimeVueInputNumber,
  },
  props: {
    modelValue: {
      type: Number,
    },
    name: {
      type: String,
    },
    errorsMap: {
      type: Object as PropType<ErrorsMap>,
      default: () => ({}),
    },
    label: {
      type: String,
    },
    help: {
      type: String,
    },
    disabled: {
      type: Boolean,
    },
    locale: {
      type: String,
    },
    mode: {
      type: String,
    },
    useGrouping: {
      type: Boolean,
      default: true,
    },
    currency: {
      type: String,
    },
    min: {
      type: Number,
    },
    max: {
      type: Number,
    },
    minFractionDigits: {
      type: Number,
    },
    maxFractionDigits: {
      type: Number,
    },
    step: {
      type: Number,
    },
    showButtons: {
      type: Boolean,
    },
    buttonLayout: {
      type: String,
    },
    incrementButtonIcon: {
      type: String,
    },
    decrementButtonIcon: {
      type: String,
    },
    prefix: {
      type: String,
    },
    suffix: {
      type: String,
    },
    inputGroup: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const wrapperClass = computed((): string => (props.inputGroup ? 'p-inputgroup' : 'field mb-0'));

    return {
      wrapperClass,
    };
  },
});
